import { Badge } from "../../ui/badge";
import { cn } from "../../utils/common/cn";
import type { ConcentrationRow } from "../../utils/portfolio-risk-concentration/api/types";

/**
 * The RAG pill used by Monitor, Capacity and Joints.
 *
 * Note ManageLimitsPage has its own StatusBadge over a different union
 * (APPROVED/REJECTED/PENDING/SUPERSEDED) — that one stays local, the two never share
 * a value space.
 */

/**
 * GREEN uses --approve rather than --foreground so a compliant figure reads as green rather
 * than as ordinary body text. Before this it was `text-foreground`, a near-white in the PRC
 * dark theme, which put a white number next to the green pill below and made "within
 * appetite" the one status with no colour of its own.
 *
 * Same token the pill uses, so the cell and the badge beside it cannot drift.
 */
/**
 * NOT_MEASURED is muted, not coloured.
 *
 * The three RAG colours each assert something about risk. "We could not measure this" asserts
 * nothing, so it gets the treatment an empty cell gets. A colour would put it in the same
 * visual language as a judgement — and amber in particular would read as a mild warning about
 * the portfolio rather than a gap in our own coverage.
 */
export function statusClass(status: ConcentrationRow["status"]) {
  return status === "RED"
    ? "text-destructive"
    : status === "AMBER"
      ? "text-amber"
      : status === "NOT_MEASURED"
        ? "text-muted-foreground"
        : "text-[color:var(--approve)]";
}

/** Sentence case: "NOT MEASURED" among one-word RAG labels reads as shouting. */
const STATUS_LABEL: Record<ConcentrationRow["status"], string> = {
  RED: "RED",
  AMBER: "AMBER",
  GREEN: "GREEN",
  NOT_MEASURED: "Not measured",
};

export function StatusBadge({
  status,
  title,
}: {
  status: ConcentrationRow["status"];
  /** Tooltip. Pass `unmeasuredReason` so the reader learns WHY there is no figure. */
  title?: string;
}) {
  return (
    <Badge
      variant="outline"
      title={title}
      className={cn(
        "text-[10px] font-semibold uppercase tracking-[0.1em]",
        status === "RED" && "border-destructive/50 bg-destructive/10 text-destructive",
        status === "AMBER" && "border-amber/50 bg-amber-dim/40 text-amber",
        status === "GREEN" &&
          "border-[color:var(--approve)]/40 bg-[color:var(--approve)]/10 text-[color:var(--approve)]",
        status === "NOT_MEASURED" &&
          "border-border bg-muted/40 text-muted-foreground normal-case tracking-normal",
      )}
    >
      {STATUS_LABEL[status]}
    </Badge>
  );
}
